import AccordionItem from '@atoms/AccordionItem/AccordionItem';
import Checkbox from '@atoms/Checkbox/Checkbox';
import ListingFilterItem from '@atoms/ListingFilterItem/ListingFilterItem';
import IFilterAccordion from '@models/IFilterAccordion';
import { IFilter } from '@models/IFilterGroup';
import { IListingFacet, IListingFacetResult } from '@models/IListing';
import Accordion from '@molecules/Accordion/Accordion';
import { useMemo, useState } from 'react';

interface Props extends IFilterAccordion {
  readonly onPromoCardReference?: (promoCardReference: number[]) => void;
  readonly promoCardRefs?: number[];
  readonly permanentFilters?: string[];
  readonly showFilterCount?: boolean;
}

interface NestedProps extends Partial<Props> {
  readonly index: number;
  readonly name: string;
  readonly filterGroup: IFilter;
  readonly handleFilterClick: (filter: IFilter) => void;
}

const createLabel = (label: string, count?: number): string => {
  return !!count ? `${label} (${count})` : label;
};

const getFacetList = (
  facets?: IListingFacet[],
  id?: string
): Array<IListingFacetResult> => {
  if (!facets?.length || !id) return [];
  return facets?.find((facet) => facet.name === id)?.results ?? [];
};

const getValidFilters = (
  allFilters: IFilter[],
  facetList: IListingFacetResult[]
) =>
  allFilters
    .map((f) => ({
      ...f,
      count: facetList.find((facet) => facet.id === f.id)?.count || 0,
    }))
    .filter((f) => f.count > 0);

const verifyStringArray = (value?: string | string[]): string[] => {
  if (!value) {
    return [];
  }
  if (typeof value === 'string') {
    return [value];
  }
  return value.filter(Boolean).flat() || [];
};

const toArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (value == null || value === false) return [];
  return [String(value)];
};

const toggleArrayValue = (arr: string[], value: string, checked: boolean) =>
  checked
    ? Array.from(new Set([...arr, value]))
    : arr.filter((v) => v !== value);

const NestedFilters = ({
  filterGroup,
  handleFilterClick,
  index,
  name,
  facets,
  permanentFilters,
  setValue,
  watch,
}: NestedProps) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const selectedParentValues = (watch?.(name) || []) as string[];

  const validFilters = useMemo(() => {
    if (facets && filterGroup.subFilters) {
      const activeFilters = verifyStringArray(
        watch?.(filterGroup.subFilters.id) as string | string[]
      );
      const facetList = getFacetList(facets, filterGroup.subFilters?.id);
      return getValidFilters(filterGroup.subFilters.options, [
        ...facetList,
        ...activeFilters.map((filter) => ({ id: filter, count: 0 })),
      ]);
    }
    return filterGroup.subFilters?.options;
  }, [filterGroup, facets]);

  const subGroupName = String(filterGroup.subFilters?.id);
  const selectedSub = toArray(watch?.(subGroupName));
  const selectedGroup = toArray(watch?.(String(name)));

  return (
    <div>
      <AccordionItem
        padding={false}
        isOpen={index === activeIndex}
        variant="nestedListingFilter"
        key={filterGroup.id}
        title={filterGroup.label}
        titleEl={
          !permanentFilters?.includes(filterGroup.id) ? (
            <Checkbox
              key={filterGroup.id}
              name={name}
              value={filterGroup.id}
              label={createLabel(filterGroup.label, filterGroup.count)}
              checked={selectedGroup.includes(filterGroup.id)}
              onChange={(e) => {
                const next = toggleArrayValue(
                  selectedGroup,
                  filterGroup.id,
                  e.currentTarget.checked
                );
                setValue?.(name, next, {
                  shouldDirty: true,
                  shouldTouch: true,
                });
                handleFilterClick({
                  ...filterGroup,
                  id: filterGroup.id,
                });
              }}
            />
          ) : undefined
        }
        callback={() => setActiveIndex(activeIndex === index ? -1 : index)}
      >
        <ul>
          {validFilters?.map((subFilter) => (
            <li key={subFilter.id} className="tw-px-4 tw-pt-8">
              <Checkbox
                key={subFilter.label}
                name={subGroupName}
                value={subFilter.id}
                label={createLabel(subFilter.label, subFilter.count)}
                onChange={(e) => {
                  if (
                    !selectedParentValues?.includes(filterGroup.id) &&
                    (e.target as HTMLInputElement).checked
                  ) {
                    setValue?.(name, [filterGroup.id, ...selectedParentValues]);
                  }
                  const next = toggleArrayValue(
                    selectedSub,
                    subFilter.id,
                    e.currentTarget.checked
                  );
                  setValue?.(subGroupName, next, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                  handleFilterClick(filterGroup);
                }}
                checked={selectedSub.includes(subFilter.id)}
              />
            </li>
          ))}
        </ul>
      </AccordionItem>

      {/* Need to include hidden checkboxes whenever the accordion is closed to ensure they are still registered to the form */}
      {index !== activeIndex && (
        <ul className="tw-hidden">
          {validFilters?.map((subFilter) => (
            <li key={subFilter.id} className="tw-px-4 tw-pt-8">
              <Checkbox
                key={subFilter.label}
                name={String(filterGroup.subFilters?.id)}
                value={subFilter.id}
                label={subFilter.label}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FilterAccordion = ({
  allowMultipleOpen = true,
  className,
  facets,
  filters,
  firstOpen = false,
  padding,
  variant = 'filter',
  watch,
  onPromoCardReference,
  promoCardRefs = [],
  permanentFilters,
  setValue,
  showFilterCount,
}: Props & { setValue: (name: string, value: any, opts?: any) => void }) => {
  const [activeIndex, setActiveIndex] = useState(firstOpen ? 0 : -1);

  const validFilters = useMemo(() => {
    if (facets) {
      const activeFilters = verifyStringArray(
        watch?.(filters.map((f) => f.id)) as string | string[]
      );
      return filters.map((f) => {
        const facetList = getFacetList(facets, f.id);
        const options = getValidFilters(f.options, [
          ...facetList,
          ...activeFilters.map((filter) => ({ id: filter, count: 0 })),
        ]);

        return { ...f, options };
      });
    }
    return filters;
  }, [filters, facets]);

  const filtersWithOptions = validFilters.filter((f) => !!f.options?.length);

  const handleFilterClick = (filter: IFilter) => {
    const ref = filter.promoCardReference;
    if (ref === undefined || !onPromoCardReference) return;

    const prev = (promoCardRefs ?? []).filter(
      (n): n is number => typeof n === 'number'
    );

    const exists = prev.includes(ref);
    const without = prev.filter((n) => n !== ref);

    const next = exists
      ? without
      : ref === 0
      ? without // don't add 0
      : [ref, ...without];

    onPromoCardReference(next);
  };

  const toggleInFormArray = (
    groupName: string,
    value: string,
    checked: boolean
  ) => {
    const current = toArray(watch?.(groupName));
    const next = toggleArrayValue(current, value, checked);
    setValue(groupName, next, { shouldDirty: true, shouldTouch: true });
  };

  return (
    <div className={className}>
      <Accordion variant={variant}>
        {filtersWithOptions.map((filterGroup, index) => {
          const groupName = String(filterGroup.id);
          const selectedValues = toArray(watch?.(groupName));
          let filterCount = 0;
          if (showFilterCount) {
            // All subfilter IDs match, so we only need the first one
            const subFilterId = filterGroup.options[0]?.subFilters?.id ?? '';
            const selectedSubFilters = verifyStringArray(
              watch?.(subFilterId) as string | string[]
            );

            filterCount = [...selectedValues, ...selectedSubFilters].filter(
              Boolean
            ).length;
          }
          return (
            <AccordionItem
              padding={padding}
              isOpen={allowMultipleOpen ? index === 0 : activeIndex === index}
              variant={variant}
              key={groupName}
              title={filterGroup.label}
              description={filterGroup.description}
              callback={() =>
                setActiveIndex(activeIndex === index ? -1 : index)
              }
              filterCount={filterCount}
            >
              <ul className="tw-no-scrollbar tw-relative tw-flex tw-flex-wrap tw-items-start tw-justify-start tw-overflow-y-auto tw-bg-white tw-px-xs tw-pb-sm">
                {filterGroup.options.map((filter, index) =>
                  filter.isShadeFilter ? (
                    <li
                      key={filter.id}
                      className="tw-group tw-flex tw-basis-1/2 tw-items-start tw-py-xxs tw-outline-none"
                    >
                      <label key={filter.id}>
                        <input
                          type="checkbox"
                          className="tw-sr-only"
                          value={filter.id}
                          checked={selectedValues.includes(filter.id)}
                          onChange={(e) => {
                            toggleInFormArray(
                              groupName,
                              filter.id,
                              e.currentTarget.checked
                            );
                            handleFilterClick(filter);
                          }}
                        />
                        <div className="tw-cursor-pointer">
                          <ListingFilterItem
                            filter={filter}
                            colourHexCode={filter.colourHexCode || ''}
                            selected={selectedValues.includes(filter.id)}
                            isDrawerFilter={true}
                          />
                        </div>
                      </label>
                    </li>
                  ) : (
                    <li className="tw-basis-full tw-py-3.5" key={filter.id}>
                      {filter.subFilters?.options.length ? (
                        <NestedFilters
                          filterGroup={filter}
                          facets={facets}
                          name={groupName}
                          watch={watch}
                          setValue={setValue}
                          handleFilterClick={handleFilterClick}
                          index={index}
                          permanentFilters={permanentFilters}
                        />
                      ) : (
                        <Checkbox
                          key={filter.id}
                          name={groupName}
                          value={filter.id}
                          label={createLabel(filter.label, filter.count)}
                          checked={selectedValues.includes(filter.id)}
                          onChange={(e) => {
                            toggleInFormArray(
                              groupName,
                              filter.id,
                              e.currentTarget.checked
                            );
                            handleFilterClick(filter);
                          }}
                        />
                      )}
                    </li>
                  )
                )}
              </ul>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};

export default FilterAccordion;
